import { z } from 'zod';
import { Permission, ProjectRole } from './rbac.js';
/**
 * Vai trò tuỳ biến trong một dự án — APM-ROLE-b.
 *
 * Ba vai trò hệ thống (`PM`/`CONTRIBUTOR`/`VIEWER`) và vai trò do PM tự tạo nằm
 * chung một bảng, phân biệt bằng `key`: khác `null` là hệ thống. Xem lý do hợp
 * nhất ở `prisma/schema.prisma`, model `ProjectRoleDefinition`.
 */
/**
 * ⚠️ Tập quyền PM được phép tự cấp — **ép ở server**, không dựa vào giao diện.
 *
 * ## Vì sao không phải toàn bộ `PERMISSIONS`
 *
 * Danh sách đầy đủ có cả quyền cấp tổ chức (`user:manage`, `ai_config:manage`,
 * `audit_log:read`…). Cho chúng vào đây nghĩa là PM của **một** dự án tự cấp
 * cho mình quyền quản trị **cả hệ thống** — một cửa hẹp mở thẳng ra cửa rộng
 * nhất có thể.
 *
 * ## Vì sao `performance:read_project` bị loại
 *
 * Dữ liệu hiệu suất cá nhân là thứ nhạy cảm nhất trong hệ thống, và nó đã có
 * đúng hai đường vào: là PM hệ thống của dự án, hoặc là quản lý trực tiếp của
 * người đó (`line_manager_id`, tầng 3 của RBAC). Một role tuỳ biến sẽ là đường
 * thứ ba mà PM **tự mở cho chính mình** — bỏ qua cả hai cửa kia.
 *
 * ## Vì sao không có `project:delete` hay `task:*` ngoài danh sách
 *
 * Chỉ những quyền mà một người **làm việc trong dự án** cần. Thêm quyền mới vào
 * `PERMISSIONS` không tự động cho nó vào đây — đó là chủ đích: danh sách này
 * phải được cân nhắc từng dòng, không phải mở rộng theo quán tính.
 */
export const CUSTOM_ROLE_SELECTABLE_PERMISSIONS = [
    'project:read',
    'project:update',
    // Cột board, tách khỏi `project:update` để cấp riêng cho Thành viên — xem `rbac.ts`.
    'workflow:manage',
    'task:create',
    'task:update',
    'task:delete',
    'task:assign',
    'document:read',
    // APM-PSPACE-i: uploading documents is no longer implied by `task:create`.
    'document:write',
    'document:approve',
    'raid:manage',
    'ai_suggestion:approve',
    'change_request:approve',
    'report:read_project',
    // Tab Thiết kế — docs/plan-design-control.md. Đây là đường vào của vai
    // Designer/BA: `design:write` không nằm ở CONTRIBUTOR mặc định.
    'design:read',
    'design:write',
    // Tab Git — docs/plan-git-integration.md §8.7. Đường vào của vai **Tech lead**:
    // `git:manage` (token, kho, luật tự đổi trạng thái) không nằm ở CONTRIBUTOR mặc
    // định. Cộng tác viên ngoài và khách hàng không nhận được ba quyền này dù vai
    // có mang (`STAFF_ONLY_PROJECT_PERMISSIONS` ở API).
    'git:read',
    'git:act',
    'git:manage',
    // Issue vận hành — docs/plan-issue-management.md §8.1. Đây là đường vào của
    // vai **Trực vận hành**: `issue:handle` không nằm ở CONTRIBUTOR mặc định
    // rộng hơn, và `issue:triage` thì chỉ PM hệ thống mới có sẵn.
    'issue:read',
    'issue:report',
    'issue:handle',
    'issue:triage',
    'issue:configure',
];
/**
 * Quyền có **phạm vi hẹp** — APM-ROLE-w.
 *
 * ## Vì sao cần, và vì sao là hậu tố chứ không phải một quyền mới
 *
 * Việc thường gặp nhất của một vai trò tuỳ biến là "làm việc được giao cho
 * mình" — đúng thứ `CONTRIBUTOR` mặc định cho. Nhưng vai trò tuỳ biến chỉ cấp
 * được `task:update` phạm vi cả dự án, nên trước bản này PM muốn dựng một vai
 * kiểu Tester chỉ có hai lựa chọn: cho sửa **mọi** việc, hoặc không cho sửa gì.
 *
 * `own` vốn đã là một `Grant` chạy được (`conditionsFor`), chỉ là không có
 * đường nào chọn nó. Nên đây là **phạm vi của một lần cấp**, không phải một
 * hành động mới: thêm `task:update_own` vào enum `Permission` sẽ tạo ra một
 * quyền mà không route nào kiểm, và người đọc ma trận quyền sau này sẽ đi tìm
 * chỗ dùng rồi không thấy.
 *
 * Cột `permissions` là JSON chuỗi nên không cần migration. ⚠️ Mọi chỗ đọc nó
 * phải biết cắt hậu tố — xem `basePermissionOf`.
 */
export const CUSTOM_ROLE_SCOPED_PERMISSIONS = [
    'task:update@own',
    /**
     * Thấy **chỉ** việc của mình — APM-GUEST-c. Vai của cộng tác viên ngoài: không
     * `project:read` (không board, không replay), nhưng vẫn mở được việc giao cho
     * mình hoặc do mình tạo. Với vai đã có `project:read` thì ô này thừa.
     */
    'task:read@own',
];
export const CustomRolePermission = z.enum([
    ...CUSTOM_ROLE_SELECTABLE_PERMISSIONS,
    ...CUSTOM_ROLE_SCOPED_PERMISSIONS,
]);
/** Hậu tố phạm vi, tách khỏi quyền gốc. `null` = không có hậu tố. */
export function basePermissionOf(value) {
    const at = value.indexOf('@');
    return at === -1
        ? { permission: value, scope: null }
        : { permission: value.slice(0, at), scope: 'own' };
}
/**
 * ⚠️ Quyền mạnh hơn vẻ ngoài của nó, phải cảnh báo ở giao diện.
 *
 * `task:update` ở role tuỳ biến luôn có phạm vi `project_member` — sửa **mọi**
 * task trong dự án. `CONTRIBUTOR` mặc định chỉ có `own` (sửa task của mình).
 * Người tạo role nhìn hai ô tích giống hệt nhau và không có cách nào đoán ra
 * khác biệt đó, nên nó phải được viết ra ngay cạnh ô tích.
 */
export const BROADER_THAN_DEFAULT = ['task:update'];
/**
 * Project permissions an outside collaborator (`GUEST`) never holds, whatever
 * their role — APM-PSPACE-h, docs/plan-personal-space.md §3.5.
 *
 * ## Why a deny-list, not an allow-list
 *
 * The user decided (2026-10-05) that a guest works "like a member, the role
 * decides": `project:read`, money, staffing — whatever the project role grants.
 * The hard ceiling only blocks **administering the project** and **decisions
 * that commit the plan**. A deny-list says exactly that; an allow-list silently
 * withholds every permission added later.
 *
 * It lives in contracts so the web can grey out roles a guest cannot hold. The
 * API still answers `GUEST_ROLE_TOO_WIDE` to direct calls (`rbac/role-ceiling.ts`),
 * and `NON_STAFF_PROJECT_CEILING` is derived from this list.
 *
 * ## Decisions guarded by a permission a guest may hold
 *
 * Passing a phase gate (`task:update`) and applying a WBS plan draft
 * (`task:create`) are commit decisions too, but forbidding those permissions
 * would take away a guest's everyday work. Their commands call
 * `assertNotCeiledDecision` (`rbac/role-ceiling.ts`) instead.
 */
export const GUEST_FORBIDDEN_PERMISSIONS = [
    /** Project administration: settings, members, roles, invite links, baseline, sprints, hand-over. */
    'project:update',
    /** Board columns — adding, renaming, re-categorising, deleting. Same class as `project:update`. */
    'workflow:manage',
    /** Approve / reject / apply change requests. */
    'change_request:approve',
    /** Commit AI suggestions into the official plan, RAID log or assignment (rule #6). */
    'ai_suggestion:approve',
    /** Approve, reject, delete and restore project documents. */
    'document:approve',
    /**
     * Administration of the issue tab: SLA, categories — and **inviting `CLIENT`
     * accounts into the organization** (`client-invite.controller.ts`). Same class
     * as the invite links behind `project:update`: an outsider must not let
     * further outsiders in.
     */
    'issue:configure',
];
/**
 * Nhóm quyền cho ô chọn — APM-ROLE-w.
 *
 * ## Vì sao gom nhóm
 *
 * Mười hai ô tích phẳng buộc người tạo vai trò đọc từng dòng và tự dựng trong
 * đầu xem nhóm nào đi với nhau. Gom theo **việc người ta làm** (xem / làm việc /
 * giấy tờ và duyệt / theo dõi) thì câu hỏi đổi từ "quyền này là gì" thành "vai
 * này có được làm nhóm việc kia không" — câu mà người tạo vai trò thật sự đang
 * trả lời.
 *
 * ## Thứ tự trong nhóm là thứ tự nguy hiểm tăng dần
 *
 * Trong mỗi nhóm, quyền chỉ-đọc đứng trước, quyền sửa/xoá đứng sau. Tích từ
 * trên xuống và dừng ở đâu cũng ra một vai trò hợp lý.
 *
 * ⚠️ Mọi quyền trong `CUSTOM_ROLE_SELECTABLE_PERMISSIONS` phải thuộc **đúng
 * một** nhóm — có test canh (`project-role.spec.ts`). Thêm quyền mới mà quên
 * xếp nhóm thì nó **biến mất khỏi giao diện**: ô chọn render theo nhóm, nên một
 * quyền không nhóm nào chứa sẽ không có ô tích nào, và không gì báo lỗi.
 */
export const CUSTOM_ROLE_PERMISSION_GROUPS = [
    { key: 'view', permissions: ['project:read', 'report:read_project'] },
    {
        key: 'work',
        // "Việc của mình" đứng **trước** "toàn dự án": đây là ô mà một vai trò kiểu
        // Thành viên cần, và để nó sau ô rộng hơn là mời người ta tích nhầm ô đầu.
        permissions: [
            'task:read@own',
            'task:create',
            'task:update@own',
            'task:update',
            'task:assign',
            'task:delete',
        ],
    },
    {
        key: 'approve',
        permissions: ['document:read', 'document:write', 'document:approve', 'change_request:approve'],
    },
    { key: 'design', permissions: ['design:read', 'design:write'] },
    // Nguy hiểm tăng dần: xem → tạo nhánh / MR, gắn liên kết → token, kho, luật.
    { key: 'git', permissions: ['git:read', 'git:act', 'git:manage'] },
    {
        key: 'service',
        /*
          Thứ tự nguy hiểm tăng dần, như mọi nhóm khác: đọc → báo → xử lý → đổi mức
          ưu tiên → viết lại thước đo của cả dự án. Tích từ trên xuống rồi dừng ở
          đâu cũng ra một vai hợp lý — dừng sau `handle` là vai Trực vận hành.
        */
        permissions: ['issue:read', 'issue:report', 'issue:handle', 'issue:triage', 'issue:configure'],
    },
    // `workflow:manage` đứng trước `project:update`: hẹp hơn, và nằm gọn trong nó.
    {
        key: 'track',
        permissions: ['raid:manage', 'ai_suggestion:approve', 'workflow:manage', 'project:update'],
    },
];
export const ProjectRoleView = z.object({
    id: z.uuid(),
    /** `null` = role tuỳ biến. Khác null = một trong ba role hệ thống. */
    key: ProjectRole.nullable(),
    name: z.string(),
    /**
     * Với role **hệ thống**, đây là bản chụp lúc tạo dự án và **không** phải thứ
     * hệ thống dùng để chặn lúc chạy — quyền của chúng đọc từ mã nguồn. Giao diện
     * hiện nó để tham khảo; đừng suy ra quyền thật từ đây.
     */
    permissions: z.array(Permission),
    isSystem: z.boolean(),
    /** Bao nhiêu người đang giữ vai trò này — để biết xoá có được không. */
    memberCount: z.number().int().nonnegative(),
    /**
     * How many of `memberCount` are outside collaborators (`GUEST` in the project's
     * organization) — APM-PSPACE-k. The Roles screen splits staff / guests, greys out
     * `GUEST_FORBIDDEN_PERMISSIONS` while a guest holds the role, and warns when such a
     * role lacks `project:read`.
     */
    guestCount: z.number().int().nonnegative().default(0),
    /**
     * Quyền **cộng thêm** của vai hệ thống ở dự án này — APM-ROLE-z. Đã nằm sẵn trong
     * `permissions` (quyền hiệu lực); trường này để giao diện biết ô nào bỏ tích được.
     * Luôn rỗng với vai tuỳ biến.
     */
    extraPermissions: z.array(CustomRolePermission).default([]),
    /** Vai hệ thống nhận quyền cộng thêm được không (`EXTENDABLE_SYSTEM_ROLES`). */
    extendable: z.boolean().default(false),
});
/**
 * Vai hệ thống PM được **cộng thêm** quyền ở dự án mình — APM-ROLE-z, người dùng
 * chốt 2026-10-14 (cho Thành viên ở vài dự án tự điền hạn + ước lượng khi bắt đầu
 * việc của người khác).
 *
 * Chỉ cộng, không bớt: quyền gốc vẫn đọc từ mã nguồn, nên sửa bảng quyền trong code
 * vẫn áp ngay cho mọi dự án (lý do vai hệ thống đọc từ code — APM-ROLE-c).
 *
 * - `PM` không có: đã có mọi quyền chọn được.
 * - `VIEWER` không có: "Chỉ xem" mà sửa được thì tên vai nói dối, và các cửa đọc
 *   `key === 'VIEWER'` (đổi trạng thái, đổi ngày) vẫn chặn — nửa mở nửa đóng. Cần
 *   vai như thế thì tạo vai tuỳ biến.
 */
export const EXTENDABLE_SYSTEM_ROLES = ['CONTRIBUTOR'];
export function isExtendableSystemRole(key) {
    return key !== null && EXTENDABLE_SYSTEM_ROLES.includes(key);
}
/** `PUT /projects/:id/roles/:roleId/extra-permissions` — APM-ROLE-z. Rỗng = bỏ hết. */
export const UpdateSystemRoleExtrasInput = z.object({
    extraPermissions: z
        .array(CustomRolePermission)
        .max(CUSTOM_ROLE_SELECTABLE_PERMISSIONS.length + CUSTOM_ROLE_SCOPED_PERMISSIONS.length),
});
export const CreateProjectRoleInput = z.object({
    name: z.string().trim().min(1).max(60),
    /*
      Trần đếm **cả** giá trị có hậu tố phạm vi.
  
      Trước đây trần là `CUSTOM_ROLE_SELECTABLE_PERMISSIONS.length`, và
      `task:update@own` thêm vào làm số giá trị tích được nhiều hơn trần đúng một
      — nên tích hết mọi ô là bị từ chối. Lỗi chỉ nổ ở đúng lựa chọn cực đoan
      nhất, thứ không ai thử tay.
    */
    permissions: z
        .array(CustomRolePermission)
        .min(1)
        .max(CUSTOM_ROLE_SELECTABLE_PERMISSIONS.length + CUSTOM_ROLE_SCOPED_PERMISSIONS.length),
});
export const UpdateProjectRoleInput = CreateProjectRoleInput;
//# sourceMappingURL=project-role.js.map